import { useState, useCallback, useRef, useEffect } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Send, MessageSquareText } from 'lucide-react';

interface CannedResponse {
  responseId: string;
  title: string;
  content: string;
}

interface TicketMessageInputProps {
  onSend?: (content: string) => Promise<void>;
  onSubmit?: (content: string) => Promise<void>;
  disabled?: boolean;
  placeholder?: string;
  cannedResponses?: CannedResponse[];
}

export function TicketMessageInput({
  onSend,
  onSubmit,
  disabled = false,
  placeholder,
  cannedResponses = [],
}: TicketMessageInputProps) {
  const handleMessage = onSend ?? onSubmit;
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSlashMenu, setShowSlashMenu] = useState(false);
  const [slashFilter, setSlashFilter] = useState('');
  const [slashIndex, setSlashIndex] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Listen for external canned response selection events (from CannedResponsePicker)
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.content) {
        setContent(detail.content);
        textareaRef.current?.focus();
      }
    };
    window.addEventListener('canned-response-selected', handler);
    return () => window.removeEventListener('canned-response-selected', handler);
  }, []);

  const filteredResponses = cannedResponses.filter((r) =>
    slashFilter
      ? r.title.toLowerCase().includes(slashFilter.toLowerCase()) ||
        r.content.toLowerCase().includes(slashFilter.toLowerCase())
      : true,
  );

  const handleContentChange = (value: string) => {
    setContent(value);

    // Detect slash command
    if (value.startsWith('/') && cannedResponses.length > 0) {
      setShowSlashMenu(true);
      setSlashFilter(value.slice(1));
      setSlashIndex(0);
    } else {
      setShowSlashMenu(false);
      setSlashFilter('');
    }
  };

  const insertCannedResponse = (response: CannedResponse) => {
    setContent(response.content);
    setShowSlashMenu(false);
    setSlashFilter('');
    textareaRef.current?.focus();
  };

  const handleSubmit = useCallback(async () => {
    const trimmed = content.trim();
    if (!trimmed || isSubmitting || !handleMessage) return;

    setIsSubmitting(true);
    try {
      await handleMessage(trimmed);
      setContent('');
      setShowSlashMenu(false);
    } finally {
      setIsSubmitting(false);
    }
  }, [content, isSubmitting, handleMessage]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (showSlashMenu && filteredResponses.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSlashIndex((prev) => (prev + 1) % filteredResponses.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSlashIndex((prev) => (prev - 1 + filteredResponses.length) % filteredResponses.length);
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        insertCannedResponse(filteredResponses[slashIndex]);
        return;
      }
      if (e.key === 'Tab') {
        e.preventDefault();
        insertCannedResponse(filteredResponses[slashIndex]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowSlashMenu(false);
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // Scroll selected item into view
  useEffect(() => {
    if (showSlashMenu && menuRef.current) {
      const item = menuRef.current.children[slashIndex] as HTMLElement;
      item?.scrollIntoView({ block: 'nearest' });
    }
  }, [slashIndex, showSlashMenu]);

  const isDisabled = disabled || isSubmitting;

  return (
    <div className="border-t border-border bg-card p-4">
      <div className="relative">
        {/* Slash command menu */}
        {showSlashMenu && filteredResponses.length > 0 && (
          <div
            ref={menuRef}
            className="absolute bottom-full left-0 right-0 mb-1 max-h-[200px] overflow-y-auto rounded-lg border bg-popover shadow-lg z-20"
          >
            {filteredResponses.map((response, index) => (
              <button
                key={response.responseId}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-muted/80 transition-colors ${
                  index === slashIndex ? 'bg-muted' : ''
                }`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  insertCannedResponse(response);
                }}
                onMouseEnter={() => setSlashIndex(index)}
              >
                <div className="flex items-center gap-2">
                  <MessageSquareText className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{response.title}</div>
                    <div className="text-xs text-muted-foreground line-clamp-1">
                      {response.content}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {showSlashMenu && filteredResponses.length === 0 && (
          <div className="absolute bottom-full left-0 right-0 mb-1 rounded-lg border bg-popover shadow-lg z-20 p-3 text-sm text-muted-foreground">
            No matching canned responses
          </div>
        )}

        <div className="flex items-end gap-2">
          <Textarea
            ref={textareaRef}
            value={content}
            onChange={(e) => handleContentChange(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isDisabled}
            placeholder={placeholder || 'Type your reply... (type / for canned responses)'}
            rows={3}
            className="resize-none"
          />
          <Button
            onClick={handleSubmit}
            disabled={isDisabled || !content.trim()}
            size="icon"
            className="flex-shrink-0"
          >
            <Send className="h-4 w-4" />
            <span className="sr-only">Send reply</span>
          </Button>
        </div>
      </div>
      <p className="mt-1.5 text-xs text-muted-foreground">
        Press Enter to send, Shift+Enter for a new line.
        {cannedResponses.length > 0 && ' Type / to insert a canned response.'}
      </p>
    </div>
  );
}
